import React, { useEffect, useState } from 'react';
import MainLayout from '@/components/Layout/MainLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { CheckCircle, XCircle, FileText, User, Calendar } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';

const ContractVerification: React.FC = () => {
  const [searchParams] = useSearchParams();
  const contractId = searchParams.get('id');
  const userId = searchParams.get('user');
  const [verified, setVerified] = useState<boolean | null>(null);
  const [userName, setUserName] = useState<string | null>(null);

  useEffect(() => {
    document.title = 'Contract Verification - Growth Energy';

    const verifyContract = async () => {
      if (!contractId || !userId) {
        setVerified(false);
        return;
      }

      // Fetch user's name from the database
      const { data: userData, error } = await supabase
        .from('users')
        .select('full_name')
        .eq('id', userId)
        .single();

      if (error || !userData) {
        console.error('Failed to fetch user:', error);
        setVerified(false);
        return;
      }

      setUserName(userData.full_name);
      setVerified(true);
    };

    verifyContract();
  }, [contractId, userId]);

  return (
    <MainLayout title="Contract Verification">
      <div className="p-4 max-w-2xl mx-auto space-y-6">
        <div className="bg-gradient-to-r from-primary/10 to-primary/5 rounded-xl p-6">
          <div className="flex items-center space-x-3 mb-2">
            <FileText className="w-6 h-6 text-primary" />
            <h2 className="text-xl font-semibold text-foreground">Contract Verification</h2>
          </div>
          <p className="text-muted-foreground">Verify the authenticity of your energy contract</p>
        </div>

        {verified === null ? (
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
              </div>
            </CardContent>
          </Card>
        ) : verified ? (
          <Card className="border-green-200 dark:border-green-800">
            <CardHeader className="bg-green-50 dark:bg-green-950/50">
              <div className="flex items-center space-x-3">
                <CheckCircle className="w-8 h-8 text-green-600 dark:text-green-400" />
                <CardTitle className="text-green-900 dark:text-green-100">Contract Verified</CardTitle>
              </div>
            </CardHeader>
            <CardContent className="pt-6 space-y-4">
              <div className="space-y-3">
                <div className="flex items-start space-x-3 p-3 bg-muted/50 rounded-lg">
                  <FileText className="w-5 h-5 text-primary mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm text-muted-foreground">Contract Number</p>
                    <p className="font-semibold text-foreground">{contractId}</p>
                  </div>
                </div>

                <div className="flex items-start space-x-3 p-3 bg-muted/50 rounded-lg">
                  <User className="w-5 h-5 text-primary mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm text-muted-foreground">Customer Name</p>
                    <p className="font-semibold text-foreground">{userName || 'Unknown'}</p>
                  </div>
                </div>

                <div className="flex items-start space-x-3 p-3 bg-muted/50 rounded-lg">
                  <Calendar className="w-5 h-5 text-primary mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm text-muted-foreground">Verified On</p>
                    <p className="font-semibold text-foreground">{new Date().toLocaleDateString()}</p>
                  </div>
                </div>
              </div>

              <div className="pt-4 border-t border-border">
                <p className="text-sm text-muted-foreground">
                  This contract is genuine and issued by Growth Energy. All contract terms and conditions apply.
                </p>
              </div>
            </CardContent>
          </Card>
        ) : (
          <Card className="border-red-200 dark:border-red-800">
            <CardHeader className="bg-red-50 dark:bg-red-950/50">
              <div className="flex items-center space-x-3">
                <XCircle className="w-8 h-8 text-red-600 dark:text-red-400" />
                <CardTitle className="text-red-900 dark:text-red-100">Verification Failed</CardTitle>
              </div>
            </CardHeader>
            <CardContent className="pt-6">
              <p className="text-muted-foreground">
                Unable to verify this contract. The QR code may be invalid or the contract information is missing.
              </p>
              <p className="text-sm text-muted-foreground mt-4">
                Please contact Growth Energy support if you believe this is an error.
              </p>
            </CardContent>
          </Card>
        )}
      </div>
    </MainLayout>
  );
};

export default ContractVerification;
